import React, { useRef, useState } from 'react';
import { API_BASE_URL } from '../../helpers/ApiSettings';
import { Editor } from '@tinymce/tinymce-react';

const EditProductModal = ({ 
  editingProduct, 
  setEditingProduct, 
  categories,
  onSubmit,
  onImageUpload,
  onImageDelete 
}) => {
  const [previewImages, setPreviewImages] = useState([]);
  const editorRef = useRef(null);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Create preview URLs for selected images
    const previews = files.map(file => ({
      url: URL.createObjectURL(file),
      file: file
    }));
    
    setPreviewImages([...previewImages, ...previews]);
    
    // Update editingProduct with the new files
    setEditingProduct({
      ...editingProduct,
      newImages: [...(editingProduct.newImages || []), ...files]
    });
  };

  const handleImageDelete = async (imageId, index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      if (imageId) {
        try {
          await onImageDelete(editingProduct.id, imageId);
          // The parent component will handle the editingProduct update
        } catch (error) {
          console.error('Error deleting image:', error);
        }
      } else {
        // Remove preview image and clean up
        setPreviewImages(prev => prev.filter((_, i) => i !== index));
        
        // Update editingProduct newImages
        const updatedNewImages = [...(editingProduct.newImages || [])];
        updatedNewImages.splice(index, 1);
        setEditingProduct({
          ...editingProduct,
          newImages: updatedNewImages
        });
        
        // Cleanup preview URL
        URL.revokeObjectURL(previewImages[index].url);
      }
    }
  };

  return (
    <div className="modal" id="editProductModal" tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          {editingProduct && (
            <form onSubmit={onSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">Modifier le produit</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
              </div>
              <div className="modal-body">
                {/* Image Management Section */}
                <div className="mb-4">
                  <label className="form-label">Images du produit</label>
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    {/* Existing Images */}
                    {editingProduct.images?.map((image, index) => (
                      <div key={image.id} className="position-relative" style={{ width: '150px', height: '150px' }}>
                        <img
                          src={`${API_BASE_URL}${image.url}`}
                          alt={`Product ${index + 1}`}
                          className="img-thumbnail"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0"
                          onClick={() => handleImageDelete(image.id)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    
                    {/* Preview Images */}
                    {previewImages.map((preview, index) => (
                      <div key={index} className="position-relative" style={{ width: '150px', height: '150px' }}>
                        <img
                          src={preview.url}
                          alt={`Preview ${index + 1}`}
                          className="img-thumbnail"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0"
                          onClick={() => handleImageDelete(null, index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mb-3">
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <small className="text-muted">
                      Vous pouvez sélectionner plusieurs images à la fois
                    </small>
                  </div>
                </div>

                {/* Product Information */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Titre</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingProduct.titre || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, titre: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Référence</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingProduct.reference || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, reference: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <Editor
                    onInit={(evt, editor) => editorRef.current = editor}
                    value={editingProduct.description || ''}
                    init={{
                      height: 300,
                      menubar: false,
                      plugins: [
                        'lists',
                        'autolink',
                        'autoresize',
                        'help',
                        'wordcount'
                      ],
                      toolbar: 'bold italic underline | ' +
                              'bullist numlist | ' +
                              'removeformat help',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                      setup: (editor) => {
                        editor.on('init', () => {
                          editor.setContent(editingProduct.description || '');
                        });
                      }
                    }}
                    onEditorChange={(content) => {
                      setEditingProduct(prev => ({...prev, description: content}));
                    }}
                  />
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Prix journalier (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={editingProduct.daily_price || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, daily_price: parseFloat(e.target.value)})}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Caution (€)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editingProduct.caution || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, caution: parseFloat(e.target.value)})}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Quantité</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editingProduct.quantity || 0}
                        onChange={(e) => setEditingProduct({...editingProduct, quantity: parseInt(e.target.value)})}
                        required
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Catégorie</label>
                      <select
                        className="form-select"
                        value={editingProduct.category?.id || ''}
                        onChange={(e) => setEditingProduct({
                          ...editingProduct,
                          category: categories.find(c => c.id === parseInt(e.target.value))
                        })}
                        required
                      >
                        <option value="">Sélectionner une catégorie</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Disponibilité</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={editingProduct.available}
                          onChange={(e) => setEditingProduct({...editingProduct, available: e.target.checked})}
                        />
                        <label className="form-check-label">
                          {editingProduct.available ? 'Disponible' : 'Indisponible'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Price Table */}
                <div className="mb-3">
                  <label className="form-label">Tarifs par durée</label>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Durée (jours)</th>
                        <th>Prix (€)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingProduct.price?.map((price, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={price.nb_days}
                              onChange={(e) => {
                                const updatedPrices = [...editingProduct.price];
                                updatedPrices[index].nb_days = parseInt(e.target.value);
                                setEditingProduct({...editingProduct, price: updatedPrices});
                              }}
                              min="1"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={price.price}
                              onChange={(e) => {
                                const updatedPrices = [...editingProduct.price];
                                updatedPrices[index].price = parseFloat(e.target.value);
                                setEditingProduct({...editingProduct, price: updatedPrices});
                              }}
                              step="0.01"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => {
                                const updatedPrices = editingProduct.price.filter((_, i) => i !== index);
                                setEditingProduct({...editingProduct, price: updatedPrices});
                              }}
                            >
                              X
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3">
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => {
                              setEditingProduct({
                                ...editingProduct,
                                price: [...(editingProduct.price || []), { nb_days: 0, price: 0 }]
                              });
                            }}
                          >
                            Ajouter un tarif
                          </button>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  Mettre à jour
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditProductModal;